<script setup lang="ts">
import IconClose from '~icons/mdi/close-circle-outline'
import type { Product as CatalogProduct } from '~/types'
import type { Product as SearchProduct } from '~/server/api/search'
import type { CampaignProductItem } from '~/composables/useCampaignProductGroups'

type FavoritableProduct = CatalogProduct | SearchProduct | CampaignProductItem

const props = defineProps<{
  isOpen: boolean
  products?: FavoritableProduct | FavoritableProduct[]
  currentPrice?: string
}>()

const emit = defineEmits<{
  (e: 'confirm' | 'cancel'): void
  (e: 'update:isOpen', value: boolean): void
  (e: 'saved', payload: { listName: string, isNewList: boolean, quantity: number }): void
}>()

const { t } = useI18n()
const shoppingListStore = useShoppingList()
const { $moment } = useNuxtApp()
const localePath = useLocalePath()

// Computed property to get the products to work with
const productsToAdd = computed(() => {
  if (!props.products) return []
  return Array.isArray(props.products) ? props.products : [props.products]
})

// Computed property to get the display product (first product for UI display)
const displayProduct = computed(() => {
  return productsToAdd.value[0] || null
})

// Computed property to check if we're adding multiple products
const isMultipleProducts = computed(() => productsToAdd.value.length > 1)

// Create a computed property with getter and setter
const dialogOpen = computed({
  get: () => props.isOpen,
  set: value => emit('update:isOpen', value),
})

// Keep track of selected shopping lists
const selectedLists = ref<string[]>(['default']) // The default list is selected by default
const isSaveDisabled = computed(() => selectedLists.value.length === 0)

// Keep track of newly created lists
const newLists = ref<string[]>([])

// Snapshot of group names captured when the dialog opens so we can detect
// which selected lists were created during this open session.
const existingGroupsAtOpen = ref<Set<string>>(new Set())

const scalePrices = computed(() => displayProduct.value?.scalePrices?.filter(item => item.minQuantity !== 1).map(item => item.minQuantity) || [])

const orderCondition = computed(() => displayProduct.value?.orderCondition)
const multiplier = computed(() => orderCondition.value?.multiplier ?? 1)
const minQuantity = computed(() => {
  if (!displayProduct.value) return 1
  return Math.max(1, orderCondition.value?.min ?? 1, multiplier.value)
})
const maxQuantity = computed(() => {
  if (!displayProduct.value) return 999
  return orderCondition.value?.max ?? 999
})

const quantity = ref(Math.max(minQuantity.value, 1))

// Computed property for btn-toggle to only select if value matches tiered quantities
const selectedScaleQuantity = computed({
  get: () => scalePrices.value.includes(quantity.value) ? quantity.value : undefined,
  set: (value) => {
    if (value !== undefined) {
      quantity.value = value
    }
  },
})

function setScaleQuantity(value: number) {
  quantity.value = value
}

function getGroupDisplayName(groupName: string) {
  return groupName === 'default' ? t('shoppingList.allFavorites') : groupName
}

const shoppingLists = computed(() => {
  const existingLists = shoppingListStore.groupNames.map((group: string) => ({
    id: group,
    name: getGroupDisplayName(group),
    selected: selectedLists.value.includes(group),
    readonly: group === 'default', // Make the default list readonly
  })) || []

  // Add locally created lists that don't exist in the store
  const localLists = newLists.value
    .filter(name => !shoppingListStore.groupNames.includes(name))
    .map(name => ({
      id: name,
      name,
      selected: selectedLists.value.includes(name),
      readonly: false,
    })) || []

  // Combine both lists
  const combinedLists = [...existingLists, ...localLists]
  // Sort to ensure the 'default' group is always first
  return combinedLists.sort((a, b) => {
    if (a.id === 'default')
      return -1
    if (b.id === 'default')
      return 1
    return 0
  })
})

// Handle shopping list selection
function handleSelectList(list: { id: string, name: string, selected: boolean, readonly?: boolean }) {
  // Skip if a list is readonly and trying to deselect it
  if (list.id === 'default' && !list.selected)
    return

  if (list.selected) {
    if (!selectedLists.value.includes(list.id)) {
      selectedLists.value.push(list.id)
    }
  }
  else {
    selectedLists.value = selectedLists.value.filter(id => id !== list.id)
  }
}

// Handle adding a new shopping list (now just client-side)
function handleAddList(name: string) {
  // Add to a local newLists array
  if (!newLists.value.includes(name)) {
    newLists.value.push(name)
  }

  // Make the new list selected
  if (!selectedLists.value.includes(name)) {
    selectedLists.value.push(name)
  }
}

function resetDialogState() {
  newLists.value = []
  selectedLists.value = ['default']
  quantity.value = 1
}

// Save the product to selected shopping lists
async function saveLists() {
  if (selectedLists.value.length > 0 && productsToAdd.value.length > 0) {
    const productHandles = productsToAdd.value.map(product => product.handle)
    for (const listName of selectedLists.value) {
      await shoppingListStore.addToShoppingList(listName, productHandles, quantity.value)
      emit('saved', {
        listName,
        isNewList: listName !== 'default' && !existingGroupsAtOpen.value.has(listName),
        quantity: quantity.value,
      })
    }

    // Emit confirm event to update the favorite state
    emit('confirm')

    // Close the dialog
    dialogOpen.value = false

    // Reset state
    resetDialogState()
  }
}

// Fetch shopping lists when the dialog opens
watch(() => dialogOpen.value, async (isOpen) => {
  if (isOpen) {
    if (shoppingListStore.shoppingLists?.length === 0) {
      await shoppingListStore.refreshInitialFetch()
    }
    existingGroupsAtOpen.value = new Set(shoppingListStore.groupNames)
    quantity.value = Math.max(minQuantity.value, 1)
  }
  else {
    existingGroupsAtOpen.value = new Set()
    // Reset state when the dialog closes
    resetDialogState()
  }
}, { immediate: true })
</script>

<template>
  <BlurDialog
    v-model="dialogOpen"
    max-width="600"
    :blur-amount="5"
  >
    <v-card class="pa-2">
      <v-card-title class="mb-3 d-flex justify-space-between align-center">
        <p class="text-h5">
          {{ $t('shoppingList.addToShoppingListDialog.title') }}
        </p>
        <v-btn
          size="small"
          :icon="IconClose"
          data-track-id="favorite_close"
          @click="dialogOpen = false"
        />
      </v-card-title>
      <!-- Multiple Products Layout -->
      <v-row v-if="isMultipleProducts" class="align-center px-4">
        <v-col cols="2">
          <NuxtImg
            :src="displayProduct?.supplier?.imageUrl ? displayProduct.supplier.imageUrl : 'https://placehold.co/20x10?text=No+image'"
            width="80"
            height="80"
            :alt="displayProduct?.supplier?.name || ''"
          />
        </v-col>
        <v-col cols="8">
          <p class="text-h6 font-weight-bold">
            {{ displayProduct?.supplier?.name }}
          </p>
        </v-col>
        <v-col cols="2" class="text-center">
          <span class="text-h6 font-weight-bold">{{ productsToAdd.length }}X</span>
        </v-col>
      </v-row>

      <!-- Single Product Layout -->
      <v-row v-else class="align-center px-4">
        <v-col cols="2">
          <VmzProductImage
            max-height="80"
            min-height="80"
            :img-src="displayProduct?.imageUrl || ''"
            :fallback-src="displayProduct?.supplier?.imageUrl || ''"
            :is-favourite="true"
            :show-favorite-btn="false"
            :alt="displayProduct?.name || ''"
          />
        </v-col>
        <v-col cols="8">
          <p>{{ displayProduct?.name }}</p>
          <div class="d-flex align-center mt-2">
            <NuxtLink
              v-if="displayProduct?.supplier?.handle"
              data-nav-id="favorite_supplier_link"
              class="text-decoration-none"
              :to="localePath(`/supplier/${displayProduct.supplier.handle}?s=${displayProduct.supplier.name}`)"
            >
              <NuxtImg
                :src="displayProduct?.supplier?.imageUrl ? displayProduct.supplier.imageUrl : 'https://placehold.co/20x10?text=No+image'"
                width="40"
                height="50"
              />
            </NuxtLink>
            <div class="px-3">
              <ProductAvailability
                v-if="displayProduct?.availability"
                :availability-status="displayProduct.availability"
                :re-availability-date="displayProduct?.dateReAvailable"
              />
              <p v-if="displayProduct?.dateBestBefore" data-test-id="product-bestBefore" class="text-caption text-vm-grey-7 ma-0">
                {{
                  $t('product.bestBefore', {
                    date: $moment(new Date(displayProduct.dateBestBefore)).utc().format(
                      $t('date.format'),
                    ),
                  })
                }}
              </p>
            </div>
          </div>
        </v-col>
        <v-col cols="2">
          {{ currentPrice }}
        </v-col>
      </v-row>
      <v-card-text>
        <v-divider class="mb-4" thickness="2" />
        <div class="shopping-list-groups">
          <v-list v-if="shoppingListStore.isLoading">
            <v-list-item v-for="n in 3" :key="n">
              <v-skeleton-loader type="text" width="100%" />
            </v-list-item>
          </v-list>
          <div v-else class="shopping-lists-container">
            <ShoppingListItem
              :list="shoppingLists"
              data-test-id="shopping-list-item"
              @select-list="handleSelectList"
              @add-item="handleAddList"
            />
          </div>
        </div>
        <div class="quantity-section py-4">
          <h5 class="font-weight-medium mb-3">
            {{ $t('shoppingList.defaultQuantityForFavorites') }}
          </h5>
          <div v-if="scalePrices.length > 0" class="d-flex align-center my-3">
            <span class="text-body-2 mr-3">{{ $t('scaleDiscount.headline') }}:</span>
            <v-btn-toggle
              v-model="selectedScaleQuantity"
              density="compact"
              color="primary"
              :show-arrows="false"
              data-test-id="shopping-list-scale-prices-button"
            >
              <v-btn
                v-for="qty in scalePrices"
                :key="qty"
                :value="qty"
                size="small"
                data-track-id="favorite_select_scale_quantity"
                @click="setScaleQuantity(qty)"
              >
                {{ qty }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <div v-if="displayProduct?.lastOrderedQuantity" class="d-flex align-center">
            <span class="text-body-2 mr-3">{{ $t('lastOrderQuantity.caption') }}:</span>
            <v-btn
              class="ml-2"
              color="primary"
              size="small"
              style="opacity: 0.75 !important;height: 36px;"
              data-test-id="shopping-list-last-ordered-quantity-button"
              :variant="quantity === displayProduct?.lastOrderedQuantity ? 'flat' : 'text'"
              data-track-id="favorite_select_last_ordered_quantity"
              @click="setScaleQuantity(displayProduct?.lastOrderedQuantity)"
            >
              {{ displayProduct?.lastOrderedQuantity }}
            </v-btn>
          </div>
          <div class="d-flex align-center mt-4" data-test-id="shopping-list-quantity-input">
            <VmzQuantityChange
              v-model:quantity-value="quantity"
              :min-quantity="minQuantity"
              :max-quantity="maxQuantity"
              :multiplier="multiplier"
              :disable-decrement-on-reach-min-quantity="true"
              variant="solo-filled"
            />
          </div>
        </div>
        <v-divider class="mb-4" thickness="2" />
        <v-btn
          color="primary"
          class="mt-4"
          size="large"
          :disabled="isSaveDisabled"
          :loading="shoppingListStore.isLoading"
          block
          data-test-id="shopping-list-save-list"
          data-track-id="favorite_save"
          @click="saveLists"
        >
          {{ $t('save') }}
        </v-btn>
      </v-card-text>
    </v-card>
  </BlurDialog>
</template>

<style lang="scss" scoped>
.shopping-lists-container {
  overflow-y: auto;
}
</style>

<style>
/* Global style for blur effect */
.dialog-blur-background > div:not(.v-dialog):not(.v-overlay__content) {
  filter: blur(4px);
  transition: filter 0.3s ease;
}

.shopping-lists-container {
  overflow-y: auto;
}
</style>
